/**
 * Design system component: ToggleButton
 */
import { memo } from "react";
import { styled } from "@mui/material";
import MuiToggleButton from "@mui/material/ToggleButton";
import type { ToggleButtonProps as MuiToggleButtonProps } from "@mui/material/ToggleButton";
import { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";

export interface ToggleButtonProps extends MuiToggleButtonProps {}

const startCorners = `&:not(.${toggleButtonGroupClasses.middleButton}):not(.${toggleButtonGroupClasses.lastButton})`;
const endCorners = `&:not(.${toggleButtonGroupClasses.firstButton}):not(.${toggleButtonGroupClasses.middleButton})`;

/**
 * @name ToggleButton
 * @description A custom styled ToggleButton component that extends the MUI ToggleButton component. This is a primitive component that should be used to create more complex components.
 */
const ToggleButton = styled((props: ToggleButtonProps) => {
  const { children, size = "medium", ...rest } = props;

  return (
    <MuiToggleButton size={size} disableFocusRipple {...rest}>
      {children}
    </MuiToggleButton>
  );
})(({ theme }) => ({
  // Default styles
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 400,
  backgroundColor: "transparent",
  border: `1px solid ${theme.palette.grey[60]}`,
  boxShadow: "none",
  color: theme.palette.primary.main,
  textAlign: "center",
  fontSize: "16px",
  lineHeight: theme.spacing(2.75),
  padding: theme.spacing(1, 2),

  [startCorners]: {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
  },

  [endCorners]: {
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },

  "&:hover": {
    backgroundColor: theme.palette.grey[20],
    border: `1px solid ${theme.palette.grey[60]}`,
  },

  "&.Mui-selected": {
    fontWeight: 500,
    // Z-index is required to ensure the pressed item border is visible, otherwise it will be hidden by the next item
    zIndex: theme.zIndex.appBar - 2,
  },

  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    zIndex: theme.zIndex.appBar - 1,
  },

  // An inset shadow thickens the border to 2px without shifting the layout
  '&[data-highlight="true"], &[data-highlight="true"]:hover': {
    borderColor: theme.palette.secondary1[100],
    boxShadow: `inset 0 0 0 1px ${theme.palette.secondary1[100]}`,
    zIndex: theme.zIndex.appBar - 2,
  },

  // Variants styles
  variants: [
    {
      props: { size: "small" },
      style: {
        fontSize: "14px",
        lineHeight: theme.spacing(2.5),
        padding: theme.spacing(0.5, 1.25),

        [startCorners]: {
          borderTopLeftRadius: theme.shape.borderRadiusSmall,
          borderBottomLeftRadius: theme.shape.borderRadiusSmall,
        },

        [endCorners]: {
          borderTopRightRadius: theme.shape.borderRadiusSmall,
          borderBottomRightRadius: theme.shape.borderRadiusSmall,
        },
      },
    },
    {
      props: { color: "primary" },
      style: {
        "&.Mui-selected": {
          backgroundColor: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.common.white,

          ":hover": {
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
    {
      props: { color: "secondary" },
      style: {
        "&.Mui-selected": {
          backgroundColor: theme.palette.secondary1[10],
          border: `1px solid ${theme.palette.secondary1[100]}`,
          color: theme.palette.primary.main,

          ":hover": {
            backgroundColor: theme.palette.secondary1[60],
          },
        },
      },
    },
  ],
}));

export default memo(ToggleButton);
